import React, { useState } from 'react';
import { Logger } from '@saviynt/common';
import { AlertBanner, AlertBannerProvider } from '@saviynt/design-system';

import testIds from '../../../../constants/testIds';
import useLocalization from '../../../../hooks/use-localize';
import { type SchedulingDetails } from '../../../ManageCMDBDiscoveredApps/data-layer/discoveryConfigByIdTypes';
import useScheduling from '../../hooks/useScheduling';
import SchedulingBody from '../SchedulingBody/SchedulingBody';
import SchedulingFooter from '../SchedulingFooter/SchedulingFooter';

import './Scheduling.css';

interface SchedulingProps {
  details: SchedulingDetails;
  status: string;
  sortOrder: number;
  instanceKey: number;
  onDirtyChange: (isDirty: boolean) => void;
  onSaveError: (message: string) => void;
  resetVersion: number;
}

/**
 * Scheduling component for managing CMDB discovery schedule configuration.
 */
function Scheduling({
  details,
  status,
  sortOrder,
  instanceKey,
  onDirtyChange,
  onSaveError,
  resetVersion,
}: SchedulingProps) {
  const t = useLocalization();

  const [localBanner, setLocalBanner] = useState<{
    kind: 'Success' | 'Critical';
    title: string;
    description: string;
    visible: boolean;
  }>({ kind: 'Success', title: '', description: '', visible: false });

  const closeLocalBanner = () =>
    setLocalBanner((b) => ({ ...b, visible: false }));

  const {
    repeatInterval,
    timeLabel,
    isDisabled,
    isSaveDisabled,
    isLoading,
    onIntervalChange,
    onEmptyChange,
    saveScheduling,
  } = useScheduling({
    cronString: details.cron_string,
    status,
    instanceKey,
    sortOrder,
    onDirtyChange,
    onSaveError: (message: string) => {
      setLocalBanner({
        kind: 'Critical',
        title: t('INTEGRATIONS.BUTTON.SAVE_ERROR_TITLE'),
        description: message,
        visible: true,
      });
      onSaveError(message);
    },
    onSaveSuccess: (message: string) => {
      setLocalBanner({
        kind: 'Success',
        title: t('INTEGRATIONS.BUTTON.SAVED', 'Saved'),
        description: message,
        visible: true,
      });
    },
    resetVersion,
  });

  return (
    <section
      className='scheduling'
      data-testid={testIds.SCHEDULING_CONTAINER}
      aria-label={t('INTEGRATIONS.SCHEDULING.ARIA_LABEL')}>
      <AlertBannerProvider>
        <AlertBanner
          colorTheme={localBanner.kind}
          title={localBanner.title}
          description={localBanner.description}
          isVisible={localBanner.visible}
          onCancel={closeLocalBanner}
          className='scheduling-alertBanner'
          dataTestId={testIds.SCHEDULING_ALERT_BANNER}
        />
      </AlertBannerProvider>
      <SchedulingBody
        repeatInterval={repeatInterval}
        onIntervalChange={onIntervalChange}
        timeLabel={timeLabel}
        disabled={isDisabled}
        onEmptyChange={onEmptyChange}
        dataTestId={testIds.SCHEDULING_BODY}
      />

      <SchedulingFooter
        onSave={() => {
          saveScheduling().catch((err: unknown) => {
            Logger.error('Save failed:', err);
          });
        }}
        isDisabled={isSaveDisabled}
        isLoading={isLoading}
        dataTestId={testIds.SCHEDULING_FOOTER}
      />
    </section>
  );
}

Scheduling.displayName = 'Scheduling';

export default React.memo(Scheduling);
